/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable */

import React, { useEffect, useState } from 'react';
import {
  EmailInput,
  FullScreenLoading,
  LargeButton,
  PassInput,
  RegularButton,
  CommonDropdown,
  ICommonDropdownOption,
  RouterLink,
} from 'pg-ui-library';
import { useTranslation } from 'react-i18next';
import logoSvg from 'src/assets/logo_pride.svg';
import background from 'src/assets/signUpBg.png';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ISecurityQuestion, ISecurityQuestionAnswer, ISignupManager } from 'src/interfaces';
import getSecurityQuestionsService from 'src/services/get-security-questions.service';
import SignupService from 'src/services/signup.service';
import { SecurityQuestionsModal } from 'src/components/security-questions-modal';

export enum Gender {
  FEMALE = 'Female',
  MALE = 'Male',
  TRANSGENDER = 'Transgender',
  NON_BINARY = 'Non binary',
  QUEER = 'QUEER',
  NOT_APPLICABLE = 'N/A',
}

export enum MaritalStatus {
  SINGLE = 'Single',
  MARRIED = 'Married',
  DIVORCED = 'Divorced',
  NOT_APPLICABLE = 'N/A',
}

export const genderOptions = Object.values(Gender);

export const maritalStatus = Object.values(MaritalStatus);

import {
  BackgroundImage,
  Container,
  FormContainer,
  MenuContainer,
  FormTitle,
  ImageContainer,
  Logo,
  UsernameInput,
  SignUpInput,
  Input,
  ActionWrapper,
  FooterContainer,
  FooterText,
  LinkContainer,
  Text,
  RedText,
  InputSelect,
} from './styles';

const ManagerSignUp: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  /** STEP CONTROL */
  const [step, setStep] = useState<1 | 2>(1);

  /** LOADING + MODAL */
  const [loading, setLoading] = useState(false);
  const [openSecurityQuestionsModal, setOpenSecurityQuestionsModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;
  const from = state?.from?.pathname || '/';
  const [signupError, setSignupError] = useState(false);

  /** SECURITY QUESTIONS */
  const [securityQuestionAnswer, setSecurityQuestionAnswer] = useState<ISecurityQuestionAnswer[]>([]);

  /** MAIN SIGNUP STATE (SINGLE SOURCE OF TRUTH) */
  const [signupValues, setSignupValues] = useState<ISignupManager>({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    middleName: '',
    gender: '',
    maritalStatus: '',
  });

  /** LOAD SECURITY QUESTIONS */
  useEffect(() => {
    const loadQuestions = async () => {
      const data: ISecurityQuestion[] = await getSecurityQuestionsService.getQuestions();

      setSecurityQuestionAnswer(
        data.map(q => ({
          questionId: q.id,
          answer: '',
          question: q.question,
        }))
      );
    };

    loadQuestions();
  }, []);

  /* ---------------- STEP 1 ---------------- */

  const step1ValidationSchema = yup.object({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    middleName: yup.string().nullable(),
    gender: yup.string().required(),
    maritalStatus: yup.string().required(),
  });

  const handleStep1Submit = (values: Partial<ISignupManager>) => {
    console.log('values', values);
    setSignupValues(prev => ({ ...prev, ...values }));
    setStep(2);
  };

  /* ---------------- STEP 2 ---------------- */

  const step2ValidationSchema = yup.object({
    email: yup.string().email().required(),
    username: yup.string().required(),
    password: yup.string().required(),
    confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match'),
  });

  const handleStep2Submit = (values: Partial<ISignupManager>) => {
    console.log('values', values);
    setSignupValues(prev => ({ ...prev, ...values }));
    setOpenSecurityQuestionsModal(true);
  };

  /* ---------------- FINAL SUBMIT ---------------- */

  const onAnswerSecurityQuestions = async (answers: ISecurityQuestionAnswer[]) => {
    const payloadsending = {
      ...signupValues,
      securityQuestionAnswer: answers,
    };
    console.log('payloadsending', payloadsending);
    try {
      setLoading(true);

      await SignupService.signupManager(
        {
          ...signupValues,
          securityQuestionAnswer: answers,
        }

        // ''
      );
      setSignupError(false);
      navigate(from, { replace: true });
    } catch (err: any) {
      setSignupError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}

      <Container>
        <MenuContainer>
          <FormContainer>
            <Logo src={logoSvg} />
            <FormTitle>{t('signUp.form.title.manager')}</FormTitle>

            {/* ================= STEP 1 ================= */}
            {step === 1 && (
              <Formik
                initialValues={{
                  firstName: signupValues.firstName,
                  lastName: signupValues.lastName,
                  middleName: signupValues.middleName,
                  gender: signupValues.gender,
                  maritalStatus: signupValues.maritalStatus,
                }}
                validationSchema={step1ValidationSchema}
                onSubmit={handleStep1Submit}
              >
                {({ handleChange, handleSubmit, setFieldValue, isValid, dirty }) => (
                  <Form onSubmit={handleSubmit}>
                    <SignUpInput>
                      <Input name="firstName" placeholder="First Name" onChange={handleChange} />
                    </SignUpInput>

                    <SignUpInput>
                      <Input name="middleName" placeholder="Middle Name" onChange={handleChange} />
                    </SignUpInput>

                    <SignUpInput>
                      <Input name="lastName" placeholder="Last Name" onChange={handleChange} />
                    </SignUpInput>

                    <SignUpInput>
                      <InputSelect
                        name="gender"
                        value={signupValues.gender}
                        onChange={e => {
                          const value = e.target.value;
                          setSignupValues(prev => ({ ...prev, gender: value }));
                          setFieldValue('gender', value);
                        }}
                      >
                        <option value="" disabled>
                          Please select Gender
                        </option>

                        {genderOptions.map(gender => (
                          <option key={gender} value={gender}>
                            {gender}
                          </option>
                        ))}
                      </InputSelect>
                    </SignUpInput>

                    <SignUpInput>
                      <InputSelect
                        name="maritalStatus"
                        value={signupValues.maritalStatus}
                        onChange={e => {
                          const value = e.target.value;
                          setSignupValues(prev => ({ ...prev, maritalStatus: value }));
                          setFieldValue('maritalStatus', value);
                        }}
                      >
                        <option value="" disabled>
                          Please select Marital Status
                        </option>

                        {maritalStatus.map(martial => (
                          <option key={martial} value={martial}>
                            {martial}
                          </option>
                        ))}
                      </InputSelect>
                    </SignUpInput>

                    <LargeButton type="submit" disabled={!isValid || !dirty}>
                      Next
                    </LargeButton>
                  </Form>
                )}
              </Formik>
            )}

            {/* ================= STEP 2 ================= */}

            {step === 2 && (
              <>
                <Formik
                  initialValues={{
                    email: signupValues.email,
                    username: signupValues.username,
                    password: signupValues.password,
                    confirmPassword: signupValues.confirmPassword,
                  }}
                  validationSchema={step2ValidationSchema}
                  onSubmit={handleStep2Submit}
                >
                  {({ handleChange, handleSubmit, isValid, dirty }) => (
                    <Form onSubmit={handleSubmit}>
                      <SignUpInput>
                        <EmailInput
                          placeholder={t('signUp.form.personalEmail')}
                          name="email"
                          handleChange={handleChange}
                        />
                      </SignUpInput>

                      <SignUpInput>
                        <UsernameInput
                          id="username"
                          name="username"
                          type="text"
                          placeholder={t('signUp.form.username')}
                          onChange={handleChange}
                        />
                      </SignUpInput>

                      <SignUpInput>
                        <PassInput
                          name="password"
                          placeholder={t('signUp.form.password')}
                          handleChange={handleChange}
                        />
                      </SignUpInput>
                      <SignUpInput>
                        <PassInput
                          name="confirmPassword"
                          placeholder={t('signUp.form.confirmPassword')}
                          handleChange={handleChange}
                        />
                      </SignUpInput>

                      <div style={{ display: 'flex', gap: 16 }}>
                        <ActionWrapper>
                          <RegularButton type="button" onClick={() => setStep(1)}>
                            {t('signUpWizard.previous')}
                          </RegularButton>
                          <RegularButton type="submit">{t('signUp.form.button')}</RegularButton>
                        </ActionWrapper>
                      </div>

                      <SecurityQuestionsModal
                        isOpen={openSecurityQuestionsModal}
                        questions={securityQuestionAnswer}
                        onAnswer={onAnswerSecurityQuestions}
                      />
                    </Form>
                  )}
                </Formik>
                {signupError && <RedText>{t('login.signUp.serverError')}</RedText>}
              </>
            )}
            <Text>{t('signUp.login.title')}</Text>
            <RouterLink to="/">{t('signUp.login.link')}</RouterLink>
          </FormContainer>
          <FooterContainer>
            <FooterText>{`PRIDE GLOBAL ${currentYear} ALL RIGHTS RESERVED`}</FooterText>
            <LinkContainer>
              <RouterLink style={{ fontSize: '12px' }} to="https://prideglobal.com/terms-of-use/" target="_blank">
                {t('signUp.footer.termsOfUse')}
              </RouterLink>
              <RouterLink style={{ fontSize: '12px' }} to="https://prideglobal.com/privacy-policy/" target="_blank">
                {t('signUp.footer.privacyPolicy')}
              </RouterLink>
              <RouterLink style={{ fontSize: '12px' }} to="mailto:employeesupport@prideglobal.com" target="_blank">
                {t('signUp.footer.support')}
              </RouterLink>
            </LinkContainer>
          </FooterContainer>
        </MenuContainer>

        <ImageContainer>
          <BackgroundImage src={background} />
        </ImageContainer>
      </Container>
    </>
  );
};

export default ManagerSignUp;
